// GenToken/validation.js

/**
 * Validates token creation form data
 * @param {Object} formData Token form data
 * @param {string} formData.tokenName Token name
 * @param {string} formData.tokenSymbol Token symbol
 * @param {number} formData.tokenDecimals Token decimals
 * @param {number} formData.tokenSupply Token initial supply
 * @param {string} formData.network Solana network to use
 * @returns {string|null} Error message or null if validation passes
 */
export const validateTokenForm = (formData) => {
    const { tokenName, tokenSymbol, tokenDecimals, tokenSupply, network } = formData;
    
    // Required fields
    if (!tokenName || !tokenSymbol) {
      return 'Token name and symbol are required';
    }
    
    // Token name validation
    if (tokenName.length < 2) {
      return 'Token name must be at least 2 characters';
    }
    
    if (tokenName.length > 50) {
      return 'Token name must be less than 50 characters';
    }
    
    // Token symbol validation
    if (tokenSymbol.length < 2) {
      return 'Token symbol must be at least 2 characters';
    }
    
    if (tokenSymbol.length > 10) {
      return 'Token symbol must be less than 10 characters';
    }
    
    // Allow only letters and numbers in symbol
    if (!/^[A-Z0-9]+$/.test(tokenSymbol)) {
      return 'Token symbol must contain only uppercase letters and numbers';
    }
    
    // Decimal validation
    if (tokenDecimals < 0 || tokenDecimals > 9) {
      return 'Token decimals must be between 0 and 9';
    }
    
    // Supply validation
    if (tokenSupply <= 0) {
      return 'Initial supply must be greater than 0';
    }
    
    if (tokenSupply > Number.MAX_SAFE_INTEGER) {
      return 'Initial supply is too large';
    }
    
    // Network validation
    const validNetworks = ['devnet', 'testnet', 'mainnet-beta'];
    if (!validNetworks.includes(network)) {
      return 'Invalid network selected';
    }
    
    return null; // Validation passed
  };
  
  /**
   * Validates a Solana public key string
   * @param {string} key Public key to validate
   * @returns {boolean} Whether the key is valid
   */
  export const isValidPublicKey = (key) => {
    // Basic validation - Solana public keys are 44 characters long Base58 strings
    if (!key || typeof key !== 'string') return false;
    
    if (key.length !== 44) return false;
    
    // Base58 character set: 123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz
    const base58Regex = /^[1-9A-HJ-NP-Za-km-z]+$/;
    return base58Regex.test(key);
  };